import { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import LandingNav from "../components/landing/LandingNav";
import LandingFooter from "../components/landing/LandingFooter";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEO title="Contact hireqix" description="Get in touch with the hireqix team for support, partnerships or press inquiries." keywords="contact hireqix, resume screener support, hireqix customer support" canonicalPath="/contact" />
      <LandingNav />
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <span className="text-xs font-bold uppercase tracking-widest text-primary">Contact Us</span>
        <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">We'd love to hear from you</h1>
        <p className="mt-4 flex items-center gap-2 text-base text-muted-foreground">
          <Mail className="h-4 w-4 text-primary" /> Or email us directly at{" "}
          <a href="mailto:support@hireqix.com" className="font-medium text-primary hover:underline" data-testid="contact-support-email-link">
            support@hireqix.com
          </a>
        </p>

        {sent ? (
          <div className="mt-10 flex items-center gap-3 rounded-2xl border border-emerald-300/60 bg-emerald-50 p-6 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="contact-success-message">
            <CheckCircle2 className="h-6 w-6 shrink-0" />
            <p className="text-sm">Thanks for reaching out! We typically respond within a few hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4 rounded-2xl border border-border/70 bg-card p-6 shadow-sm sm:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              <Input type="email" required placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
            <Textarea required placeholder="How can we help?" className="min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
            <Button type="submit" disabled={loading} size="lg" data-testid="contact-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}
            </Button>
          </form>
        )}
      </section>
      <LandingFooter />
    </div>
  );
}
